/**
 * Model Role constants — defines the purpose-based model slots
 * that users configure to orchestrate tasks across different model classes.
 *
 * Each role has a label, icon, description (shown in UI), and a fallback
 * role if the user has not configured it.
 */

import type { ModelRole } from '../types/ai';

export interface ModelRoleDefinition {
  /** Human-readable label */
  label: string;
  /** Emoji icon for UI display */
  icon: string;
  /** Explains to the user what this role is used for */
  description: string;
  /** If this role is not configured, fall back to this role. null = required. */
  fallback: ModelRole | null;
}

export const MODEL_ROLES: Record<ModelRole, ModelRoleDefinition> = {
  vlm: {
    label: 'Vision (VLM)',
    icon: '🖼️',
    description:
      'Image captioning, logo recognition, figure and diagram analysis in documents.',
    fallback: 'medium',
  },
  small: {
    label: 'Small (Fast)',
    icon: '⚡',
    description:
      'Quick classification, entity tagging, label extraction, simple yes/no decisions.',
    fallback: 'medium',
  },
  medium: {
    label: 'Medium (Balanced)',
    icon: '🔄',
    description:
      'Document summarization, table extraction, knowledge base processing.',
    fallback: null, // Required — this is the minimum configuration
  },
  large: {
    label: 'Large (Reasoning)',
    icon: '🧠',
    description:
      'Ontology generation, complex chat reasoning, multi-step extraction.',
    fallback: 'medium',
  },
} as const;

/** Ordered list of roles for UI rendering */
export const MODEL_ROLE_ORDER: ModelRole[] = ['vlm', 'small', 'medium', 'large'];

/**
 * VISION CAPABILITY IS NOT DECIDED HERE ANY MORE (root BACKLOG §29, removed 2026-08-24).
 *
 * `VISION_CAPABLE_MODELS` was an allowlist of 22 model IDs and `isVisionCapableModel` matched against it.
 * Measured 2026-08-23: it answered FALSE for `claude-sonnet-5`, the model this platform is configured with,
 * so the backend never sent a single image to a VLM and every figure was captioned from the text around it,
 * silently. The list also drove a UI warning, which meant the one model that works was the one flagged as
 * probably broken.
 *
 * The capability is now DISCOVERED: the layers worker sends the image and reads whether the gateway refuses
 * the modality, remembers that per model, and records in each caption's metadata whether the bytes were
 * actually looked at (`used_vlm`). A list of names cannot know what a gateway added last week, and it fails
 * in the direction that looks like success.
 */

// Made with Bob
